import React, { lazy, Suspense } from "react";
import { Box } from "@mui/material";
import Navbar from "../widgets/navbar/navbar";
import MenuDrawer from "../../../Components/Widgets/MenuDrawer";
import SubMenuDrawer from "../../../Components/Widgets/SubMenuDrawer";
import Footer from "../widgets/footer";
import RevealSection from "../widgets/common/reveal-section";
import { HeroPlaceholder } from "../widgets/common/home-section-skeleton";
import { useTheme } from "@mui/material/styles";

const DualImageHeroSlide = lazy(
  () => import("../../default/widgets/sliders/dual-image-hero-slide"),
);

const ImageBackroundEffect = lazy(
  () => import("../../default/widgets/sliders/image-backround-effect"),
);

const ProductsShowcaseSlider = lazy(
  () => import("../../../Components/Widgets/Sliders/ProductsShowcaseSlider"),
);

const ShopCategoryGrid = lazy(
  () => import("../../default/widgets/sliders/shop-category-grid"),
);

const ExploreMoreSection = lazy(
  () => import("../../default/widgets/sliders/explore-more-section"),
);

const RecentlyViewed = lazy(
  () => import("../../../Components/Widgets/RecentlyViewed"),
);

const ExportDefault = (props) => {
  const theme = useTheme();
  const gap = { xs: 6, md: 8 };
  const pairGap = { xs: 3, md: 4 };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Navbar bg_color="#fff" {...props} />
      <MenuDrawer {...props} />
      <SubMenuDrawer {...props} />
      <Suspense fallback={<HeroPlaceholder theme={theme} />}>
        <DualImageHeroSlide
          {...{
            ...props,
            mb: gap,
            slide: {
              imagesPath: "/marketing/main",
              images: {
                main: "women-stacking-main",
                secondary: "women-stacking-secondary",
              },
              title: "STACKING WITH INTENTION",
              desc: "Discover the art of layering with our signature necklaces and rings, crafted to bring depth, texture, and meaning to every look.",
              descMaxWidth: { xs: "unset", sm: "600px" },
              buttons: [
                {
                  name: "STACKABLE NECKLACES",
                  url: "/women/curation/stacking?type=necklace",
                },
                {
                  name: "STACKABLE RINGS",
                  url: "/women/curation/stacking?type=ring",
                },
              ],
            },
          }}
        />
      </Suspense>

      <RevealSection id="categoriesAndMaterials" variant="slider">
        <ShopCategoryGrid
          {...{
            ...props,
            mb: gap,
            title: "SHOP BY",
            imagesPath: "/marketing/main",
            tabs: [
              {
                slug: "gategory",
                name: "CATEGORY",
                // url: "/women/types/all",
                slides: [
                  {
                    slug: "womens-rings",
                    title: "Rings",
                    url: "/women/type/ring",
                  },
                  {
                    slug: "womens-pendants",
                    title: "Pendants",
                    url: "/women/type/pendant",
                  },
                  {
                    slug: "womens-bracelets",
                    title: "Bracelets",
                    url: "/women/type/bracelet",
                  },
                  {
                    slug: "womens-earrings",
                    title: "Earrings",
                    url: "/women/type/earrings",
                  },
                  {
                    slug: "womens-necklaces",
                    title: "Necklaces",
                    url: "/women/type/necklace",
                  },
                  {
                    slug: "womens-shop-all",
                    title: "All",
                    url: "/women/types/all",
                    showOnDesktop: false,
                  },
                ],
              },
              {
                slug: "material",
                name: "MATERIAL",
                // url: "/men/types/all",
                slides: [
                  {
                    slug: "women-gold",
                    title: "Gold",
                    url: "/women/metal/18kt-gold",
                  },
                  {
                    slug: "women-mixed-metals",
                    title: "Mixed Metals",
                    url: "/women/metal/mixed-metals",
                  },
                  {
                    slug: "women-sterling-silver",
                    title: "Sterling Silver",
                    url: "/women/metal/sterling-silver",
                  },
                  {
                    slug: "women-leather",
                    title: "Leather",
                    url: "/women/metal/leather",
                  },
                ],
              },
            ],
          }}
        />
      </RevealSection>

      <RevealSection
        id="imageBackgroundBestSellers"
        variant="banner"
        minHeight="100svh"
        mode="load-only"
      >
        <ImageBackroundEffect
          {...{
            ...props,
            mb: pairGap,
            slide: {
              slug: "women-best-sellers",
              url: "/",
              imagesPath: "/marketing/main",
              content: {
                title: "BEST SELLERS",
                desc: "Explore our top-selling designs, crafted with timeless detail and signature artistry. From everyday statements to meaningful gifts, each piece is made to stand out and be cherished.",
                descMaxWidth: { xs: "unset", sm: "500px" },
                buttons: [
                  {
                    name: "SHOP BEST SELLERS",
                    url: "/women/curation/best-sellers",
                  },
                ],
                position: {
                  mobile: {
                    bottom: "40px",
                    left: "24px",
                    right: "24px",
                  },
                  desktop: {
                    bottom: "100px",
                    right: { md: "40px", lg: "80px" },
                  },
                },
              },
            },
          }}
        />
      </RevealSection>

      <RevealSection id="bestSellersProducts" variant="products">
        <ProductsShowcaseSlider
          {...{
            ...props,
            mb: gap,
            params: {
              pageAmount: 100,
              curation: "best-sellers",
              gender: "women",
              type: "ring,bracelet,earrings,pendant,necklace",
            },
            amount: 15,
            shuffle: true,
            productDisplay: {
              showName: false,
              showCollection: false,
              showPrice: false,
              showGems: false,
              showCuration: false,
            },
          }}
        />
      </RevealSection>

      <RevealSection id="featuredSlider" variant="slider">
        <ExploreMoreSection
          {...{
            ...props,
            mb: gap,
            title: "EXPLORE WOMEN'S SIGNATURE DESIGNS",
            imagesPath: "/marketing/main",
            slides: [
              {
                slug: "women-sacred-symbols",
                url: "/women/curation/sacred-symbols",
                buttons: [
                  {
                    name: "Sacred Symbols",
                    url: "/women/curation/sacred-symbols",
                  },
                ],
              },
              {
                slug: "women-pearl-designs",
                url: "/women/curation/pearl-designs",
                buttons: [
                  {
                    name: "Pearl Designs",
                    url: "/women/curation/pearl-designs",
                  },
                ],
              },
              {
                slug: "women-birthstones",
                url: "/women/collection/birthstone",
                buttons: [
                  {
                    name: "evil eye Birth Stones",
                    url: "/women/collection/birthstone",
                  },
                ],
              },
              {
                slug: "women-zodiacs",
                url: "/women/collection/zodiac",
                buttons: [
                  {
                    name: "Zodiacs SIGNS",
                    url: "/women/collection/zodiac",
                  },
                ],
              },
            ],
          }}
        />
      </RevealSection>

      <RevealSection
        id="imageBackgroundNewDesigns"
        variant="banner"
        minHeight="100svh"
        mode="load-only"
      >
        <ImageBackroundEffect
          {...{
            ...props,
            mb: pairGap,
            slide: {
              slug: "women-new-designs",
              url: "/",
              imagesPath: "/marketing/main",
              content: {
                title: "NEW FROM KONSTANTINO",
                desc: "Discover stunning new creations from our latest women’s collections, crafted with refined detail, signature artistry, and effortless elegance.",
                descMaxWidth: { xs: "unset", sm: "420px" },
                buttons: [
                  {
                    name: "SHOP NEW DESIGNS",
                    url: "/women/curation/new-arrivals",
                  },
                ],
                position: {
                  mobile: {
                    bottom: "40px",
                    left: "24px",
                    right: "24px",
                  },
                  desktop: {
                    bottom: "100px",
                    left: { md: "40px", lg: "80px" },
                  },
                },
              },
            },
          }}
        />
      </RevealSection>

      <RevealSection id="nedaProducts" variant="products">
        <ProductsShowcaseSlider
          {...{
            ...props,
            mb: gap,
            params: {
              pageAmount: 100,
              curation: "new-arrivals",
              gender: "women",
              type: "ring,bracelet,earrings,pendant,necklace",
            },
            amount: 15,
            shuffle: true,
            productDisplay: {
              showName: false,
              showCollection: false,
              showPrice: false,
              showGems: false,
              showCuration: false,
            },
          }}
        />
      </RevealSection>

      <RevealSection id="womenCollections" variant="slider">
        <ExploreMoreSection
          {...{
            ...props,
            mb: gap,
            title: "SHOP WOMEN'S COLLECTIONS",
            imagesPath: "/marketing/collections",
            buttons: [
              {
                name: "VIEW ALL",
                url: "/women/collectionS",
              },
            ],
            slides: [
              {
                slug: "theodora-collection",
                url: "/women/collection/theodora",
                buttons: [
                  {
                    name: "THEODORA COLLECTION",
                    url: "/women/collection/theodora",
                  },
                ],
                description:
                  "Step into the luminous elegance of Theodora, where Byzantine-inspired details and delicate gold forms create designs of quiet power and refined beauty.",
              },
              {
                slug: "neda-collection",
                url: "/women/collection/neda",
                buttons: [
                  {
                    name: "SHOP NEDA",
                    url: "/women/collection/neda",
                  },
                ],
                description:
                  "Discover Neda, where hammered surfaces, radiant stones, and Greek-inspired details create jewelry with texture, depth, and refined presence.",
              },
              {
                slug: "rhea-collection",
                url: "/women/collection/rhea",
                buttons: [
                  {
                    name: "SHOP RHEA",
                    url: "/women/collection/rhea",
                  },
                ],
                description:
                  "Rhea captures color in motion, with bold stones and sculptural silhouettes designed to reveal depth, strength, and elegance.",
              },
              {
                slug: "kyma-collection",
                url: "/women/collection/kyma",
                buttons: [
                  {
                    name: "SHOP KYMA",
                    url: "/women/collection/kyma",
                  },
                ],
                description:
                  "Discover designs meticulously crafted to capture the Aegean Sea’s waves with clean lines, smooth curves, and sculptural forms.",
              },
              {
                slug: "amazona-collection",
                url: "/women/collection/amazona",
                buttons: [
                  {
                    name: "AMAZONA COLLECTION",
                    url: "/women/collection/amazona",
                  },
                ],
                description:
                  "Discover the Amazona Collection, where classic influences are shaped into a distinctly modern statement.",
              },
            ],
          }}
        />
      </RevealSection>

      <RevealSection id="recentlyViewed" variant="products">
        <RecentlyViewed
          {...props}
          mb={gap}
          title="ITEMS YOU VIEWED"
          productDisplay={{
            showName: false,
            showCollection: false,
            showPrice: false,
            showGems: false,
            showCuration: true,
          }}
        />
      </RevealSection>

      <Footer {...props} />
    </Box>
  );
};

export default ExportDefault;
